import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Avatar from "./Avatar";

// Fixed positions for confetti pieces — random per mount but stable per
// frame to keep the animation deterministic across re-renders.
function makePieces(count, variant) {
    const goldPalette = ["#FFD23F", "#F2B632", "#E1A12A", "#FFE783", "#C99326"];
    const partyPalette = ["#FFD23F", "#FF7A6E", "#7FB069", "#7C9CBF", "#E8A1B6", "#F2B632"];
    const palette = variant === "gold" ? goldPalette : partyPalette;
    return Array.from({ length: count }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 0.6,
        dur: 2.2 + Math.random() * 1.6,
        size: 7 + Math.random() * 6,
        rotate: Math.random() * 360,
        rotateEnd: 360 + Math.random() * 720,
        drift: (Math.random() - 0.5) * 90,
        color: palette[i % palette.length],
        // squares + circles mixed for visual richness
        shape: Math.random() < 0.5 ? "square" : "circle",
    }));
}

/**
 * MilestoneCelebration — full-screen confetti + Mochi + rotating phrase.
 *
 * The parent (App.js) controls open/close via the watcher hook. We render
 * confetti pieces purely with CSS keyframes via framer-motion's animate
 * shortcut so the compositor handles motion (no JS per-frame work).
 *
 * Auto-dismiss after `durationMs`; calls `onComplete()` so the parent can
 * mark the milestone seen + seed the chat message + transition Mochi to
 * the post-celebration mood.
 */
export default function MilestoneCelebration({
    open, milestone, phrase, durationMs = 3000, avatarStyle = "feminine", skin = null,
    onComplete,
}) {
    const piecesRef = useRef(null);
    if (!piecesRef.current && open) {
        const variant = milestone?.confettiVariant || "regular";
        const count = milestone?.day >= 365 ? 80 : milestone?.day >= 100 ? 70 : 50;
        piecesRef.current = makePieces(count, variant);
    }
    if (!open && piecesRef.current) piecesRef.current = null;

    useEffect(() => {
        if (!open) return undefined;
        const t = setTimeout(() => onComplete?.(), durationMs);
        return () => clearTimeout(t);
    }, [open, durationMs, onComplete]);

    return (
        <AnimatePresence>
            {open && milestone && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.35 }}
                    className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-cream/80 backdrop-blur-md"
                    data-testid={`milestone-celebration-${milestone.kind === "unlock" ? milestone.id : milestone.day}`}
                    aria-live="polite"
                >
                    {/* Confetti layer */}
                    <div className="pointer-events-none absolute inset-0 overflow-hidden">
                        {piecesRef.current?.map((p) => (
                            <motion.span
                                key={p.id}
                                initial={{ y: -40, x: 0, rotate: p.rotate, opacity: 0 }}
                                animate={{ y: "110vh", x: p.drift, rotate: p.rotateEnd, opacity: [0, 1, 1, 0.4, 0] }}
                                transition={{
                                    duration: p.dur,
                                    delay: p.delay,
                                    ease: "easeIn",
                                    times: [0, 0.05, 0.6, 0.9, 1],
                                }}
                                style={{
                                    position: "absolute",
                                    left: `${p.left}%`,
                                    top: 0,
                                    width: p.size,
                                    height: p.size,
                                    background: p.color,
                                    borderRadius: p.shape === "circle" ? "50%" : "2px",
                                }}
                            />
                        ))}
                    </div>

                    {/* Mochi + message — sits above the confetti */}
                    <div className="relative z-10 flex flex-col items-center px-6 max-w-md text-center">
                        <p
                            className="font-display text-xs uppercase tracking-[0.3em] text-warm-500 mb-2"
                            data-testid="milestone-day-label"
                        >
                            {milestone.name}
                        </p>
                        <motion.div
                            initial={{ scale: 0.6, y: 20, opacity: 0 }}
                            animate={{ scale: 1, y: 0, opacity: 1 }}
                            transition={{ type: "spring", stiffness: 260, damping: 16 }}
                        >
                            <Avatar
                                mood={milestone.initialMood}
                                size={220}
                                style={avatarStyle}
                                skin={skin}
                                accessory={milestone.unlock === "crown" ? "crown" : milestone.unlock === "birthday-hat" ? "birthday-hat" : null}
                                pin={milestone.unlock === "sakura-pin"}
                                accessoryAnimateIn
                            />
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4, duration: 0.45 }}
                            className="mt-4 bg-cream rounded-clay px-5 py-4 shadow-clay border border-warm-300/30"
                            data-testid="milestone-bubble"
                        >
                            <p className="font-body text-base text-warm-900 leading-snug">
                                {phrase}
                            </p>
                        </motion.div>
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 1.2 }}
                            className="mt-4 font-body text-xs text-warm-500 italic"
                        >
                            mochi sent you something in chat 💌
                        </motion.p>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
